import { Info } from 'lucide-preact';
import { createPortal } from 'preact/compat';
import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { getOverviewBlockInfo, getOverviewBlockTitle } from '../../content';
import { cn } from './types';

interface BlockInfoIconProps {
  blockId: string;
  info?: string;
  label?: string;
  className?: string;
}

interface PopoverPosition {
  top: number;
  left: number;
}

function computePopoverPosition(button: HTMLButtonElement): PopoverPosition {
  const rect = button.getBoundingClientRect();
  const popoverWidth = 224;
  const horizontalPadding = 8;
  const centeredLeft = rect.left + rect.width / 2;
  const minLeft = horizontalPadding + popoverWidth / 2;
  const maxLeft = window.innerWidth - horizontalPadding - popoverWidth / 2;

  return {
    top: rect.bottom + 8,
    left: Math.min(Math.max(centeredLeft, minLeft), maxLeft),
  };
}

export function BlockInfoIcon({ blockId, info, label, className }: BlockInfoIconProps) {
  const [popoverPosition, setPopoverPosition] = useState<PopoverPosition | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const hideTimeoutRef = useRef<number | undefined>(undefined);
  const popoverId = useId();
  const resolvedInfo = info ?? getOverviewBlockInfo(blockId);
  const ariaLabel = label ?? `About ${getOverviewBlockTitle(blockId, 'this section')}`;
  const isOpen = popoverPosition !== null;

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current !== undefined) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }
  };

  const showTooltip = () => {
    clearHideTimeout();
    const button = buttonRef.current;
    if (!button) return;
    setPopoverPosition(computePopoverPosition(button));
  };

  const hideTooltip = () => {
    clearHideTimeout();
    hideTimeoutRef.current = window.setTimeout(() => {
      setPopoverPosition(null);
      hideTimeoutRef.current = undefined;
    }, 80);
  };

  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const button = buttonRef.current;
      if (!button) return;
      setPopoverPosition(computePopoverPosition(button));
    };

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  useEffect(() => () => clearHideTimeout(), []);

  if (!resolvedInfo) {
    return null;
  }

  return (
    <>
      <div className={cn('block-info-icon', className)}>
        <button
          ref={buttonRef}
          type="button"
          className={`block-info-icon__button${isOpen ? ' is-active' : ''}`}
          aria-label={ariaLabel}
          aria-describedby={isOpen ? popoverId : undefined}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onFocus={showTooltip}
          onBlur={hideTooltip}
        >
          <Info size={14} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div
            id={popoverId}
            className="block-info-icon__popover block-info-icon__popover--fixed"
            role="tooltip"
            style={{
              top: `${popoverPosition.top}px`,
              left: `${popoverPosition.left}px`,
            }}
          >
            <p>{resolvedInfo}</p>
          </div>,
          document.body
        )}
    </>
  );
}
